import { CustomField } from '../types/custom-fields/types';
import { getSuffixOptions, getTitleOptions } from './getOptions';
import { stateOptions } from './stateOptions';

type Option = { label: string; value: string };

interface StateData {
  name: string;
  markets: { id: string; name: string }[];
}

// Map custom field names to filter UI names
const FIELD_NAME_TO_FILTER_NAME: Record<string, string> = {
  'Takes-Walk-Ins': 'Takes Walkins',
  'New-Telehealth': 'New Telehealth',
  'Flu-Shots': 'Flu Shots',
  'New-Patient': 'New Patient',
  'Other-Languages': 'Other Languages',
  'ADD-ADHD': 'ADD/ADHD',
  'Chronic-Pain-Management': 'Chronic Pain Management',
  'Unvaccinated-Children': 'Unvaccinated Children',
  'OB-Visits': 'OB Visits',
  'Workers-Comp': 'Workers Comp',
  'Disability-Physical': 'Disability Physical',
  'Military-Physical': 'Military Physical',
  'Flight-Physical': 'Flight Physical',
  'DOT-Physical': 'DOT Physical',
  'Immigration-Physical': 'Immigration Physical',
  Pap: 'PAP',
};

/**
 * Check if a field is a yes/no field based on its allowed_values
 */
const isYesNoField = (field: CustomField): boolean => {
  if (!field.allowed_values || field.allowed_values.length === 0) {
    return false;
  }

  const nonEmptyValues = field.allowed_values.filter(
    (v) => v.value && v.value.trim() !== ''
  );

  if (nonEmptyValues.length === 0) {
    return false;
  }

  // Check if all values are Yes/No (case-insensitive)
  return nonEmptyValues.every((v) => {
    const normalized = String(v.value).trim().toLowerCase();
    return normalized === 'yes' || normalized === 'no';
  });
};

/**
 * Get filter options from allowed_values
 */
export const getOptionsFromAllowedValues = (
  allowedValues: { id: string; sort_order: number; value: string }[]
): Option[] => {
  return [...allowedValues]
    .filter((v) => v.value && v.value.trim() !== '')
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((item) => ({
      label: item.value,
      value: item.value,
    }));
};

/**
 * Build filter options dynamically from custom fields
 */
export const buildFilterOptionsFromCustomFields = (
  customFields: CustomField[] | undefined | null,
  statesData: StateData[],
  specialtyOptions: Option[] = [],
  includeStates: boolean = false,
  isPractice: boolean = false
): Record<string, Option[]> => {
  const filterOptions: Record<string, Option[]> = {};

  // Always include Market (from statesData) - regardless of customFields
  filterOptions.Market = statesData
    .flatMap((state) =>
      state.markets.map((market) => ({
        label: market.name,
        value: market.id,
      }))
    )
    .sort((a, b) => a.label.localeCompare(b.label));

  // Include States if requested (for Practices) - regardless of customFields
  if (includeStates) {
    filterOptions.States = stateOptions;
  }

  // Always include Specialty if options are provided - regardless of customFields
  if (specialtyOptions.length > 0) {
    filterOptions.Specialty = specialtyOptions;
  }

  // Include Suffix and Title only for Providers (not Practices) - regardless of customFields
  // These are provider-specific filters that should always be present
  if (!isPractice) {
    filterOptions.Suffix = getSuffixOptions();
    filterOptions.Title = getTitleOptions();
  }

  // Early return if no custom fields - but Market/States/Specialty/Suffix/Title already added above
  if (!customFields || customFields.length === 0) {
    return filterOptions;
  }

  // Fields to exclude from Practices filters
  const practiceExcludedFields = [
    'Confirm-Enabled',
    'Cancel-Enabled',
    'Make-Enabled',
    'Reschedule-Enabled',
    'Notes-Category', // Backend doesn't support Notes Category filter yet
  ];

  // Process custom fields
  customFields.forEach((field) => {
    // Skip fields that shouldn't be filters
    if (
      field.name === 'Provider-Type' ||
      field.name === 'Triage-Triggers' ||
      field.name === 'Office-Notes' ||
      field.name === 'Daily-Alert' ||
      field.name === 'Scheduling-Notes' ||
      field.name === 'Notes-Category' || // Exclude Notes Category for all entities
      !field.active
    ) {
      return;
    }

    // Skip "Enabled" fields and Notes Category for Practices
    if (isPractice && practiceExcludedFields.includes(field.name)) {
      return;
    }

    // Skip Specialty field - it's handled separately via specialtyOptions parameter
    if (field.name === 'Specialty' || field.field_type === 'Specialty') {
      return;
    }

    // Get filter name (use mapping or convert field name)
    const filterName =
      FIELD_NAME_TO_FILTER_NAME[field.name] ||
      field.name.replace(/-/g, ' ');

    // Skip if already added (e.g., Market, States)
    if (filterOptions[filterName]) {
      return;
    }

    // Handle fields with allowed_values
    if (field.allowed_values && field.allowed_values.length > 0) {
      if (isYesNoField(field)) {
        // Yes/No fields
        filterOptions[filterName] = [
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' },
        ];

        // Special handling for fields that might have additional values
        if (field.name === 'Flu-Shots') {
          const hasLimited = field.allowed_values.some(
            (v) =>
              v.value &&
              String(v.value).trim().toLowerCase() === 'limited'
          );
          if (hasLimited) {
            filterOptions[filterName].push({
              label: 'Limited',
              value: 'Limited',
            });
          }
        }

        if (field.name === 'New-Patient') {
          const hasPriority = field.allowed_values.some(
            (v) =>
              v.value &&
              String(v.value).trim().toLowerCase() === 'priority'
          );
          if (hasPriority) {
            filterOptions[filterName].push({
              label: 'Priority',
              value: 'Priority',
            });
          }
        }
      } else {
        // Fields with custom allowed_values
        filterOptions[filterName] = getOptionsFromAllowedValues(
          field.allowed_values
        );
      }
    }
  });

  return filterOptions;
};

